import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { cn } from "@/shared/lib/utils";
import { useImagingStudies } from "@/portals/patient/context/ImagingStudiesContext";
import type { ActiveImagingBatch } from "@/portals/patient/context/ImagingStudiesContext";
import { getSeriesGroupId, type ImagingSeries } from "@/portals/patient/api/imagingStudiesApi";
import { InfiniteScrollContainer } from "@/shared/components/InfiniteScrollContainer";

// Row height (~70px: 16px padding top+bottom + a ~38px two-line text block)
// plus 12px gaps between rows, rounded up per breakpoint — 3 rows fit before
// scrolling on a small/laptop screen, more on a bigger desktop viewport,
// rather than a "3, always" cap regardless of how much room is actually
// available. Static string literal (not interpolated) so Tailwind's
// build-time scanner generates these classes.
const SCROLL_HEIGHT_CLASSES = "max-h-[236px] lg:max-h-[400px] xl:max-h-[560px] 2xl:max-h-[720px]";

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** One in-flight DICOM batch's row — rendered ABOVE the normal completed-
 *  series rows below (see the card's own render), so a batch the patient
 *  just submitted shows up immediately rather than waiting to appear once
 *  finished. Same border/padding/icon-circle shell as a completed row (see
 *  the button rows further down), but deliberately NOT a <button> — there's
 *  nothing to navigate to yet — and uses the same spinning lucide:loader-2 +
 *  "Processing X of Y" language ImagingUploadModal's own old in-modal
 *  progress view used, just moved here rather than reinvented: this is the
 *  same concept relocated, not a new one. */
function ActiveBatchRow({ batch }: { batch: ActiveImagingBatch }) {
  const percent = batch.totalExpected > 0 ? Math.round((batch.processedCount / batch.totalExpected) * 100) : 0;
  return (
    <div className="flex w-full items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-[#FCFCFC] px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F7F7F7]">
        <Icon icon="lucide:loader-2" className="h-4 w-4 animate-spin !text-[#E4002B]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-[#0A2947]">{batch.displayName}</p>
        <p className="text-xs font-semibold text-[#E4002B]">
          {batch.status === "queued"
            ? "Queued…"
            : `Processing ${batch.processedCount} of ${batch.totalExpected} (${percent}%)`}
        </p>
      </div>
    </div>
  );
}

/**
 * Every currently-loaded DICOM study — the right-half counterpart to
 * RecentUploadsCard's medical-records list, shown side by side with it on
 * the Upload Records page. As many rows as fit the screen show in full, the
 * rest scroll (see SCROLL_HEIGHT_CLASSES) rather than being cut off at a
 * fixed count. Reads from the shared ImagingStudiesContext (mounted once in
 * PatientProtectedLayout.tsx), so this stays in sync with the full list on
 * Diagnostics & Imaging without a second fetch. Renders nothing once loaded
 * if the patient has no studies yet AND no batch is currently in flight,
 * same "renders nothing" convention as RecentUploadsCard otherwise follows.
 *
 * ACTIVE BATCHES: any batch submitted via ImagingUploadModal but not yet
 * finished (see ImagingStudiesContext.activeBatches) renders as an
 * ActiveBatchRow ABOVE the completed rows below — this is the ONE place the
 * "still working" batch state surfaces (see DiagnosticsImagingSection's own
 * note on why it's scoped to just this card, not the Diagnostics & Imaging
 * page's list too).
 */
export function RecentDicomUploadsCard({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { series, isLoading, activeBatches } = useImagingStudies();
  const recent = series;

  const openViewer = (found: ImagingSeries) => {
    // Takes the clicked series object directly now, NOT a seriesInstanceUid
    // to look up — two distinct rows in `series` can share that raw tag
    // (see getSeriesGroupId's own docstring), so a find-by-tag lookup could
    // silently resolve to the WRONG row (always the first tag match) when
    // that happens. The caller already has the exact row it rendered; no
    // lookup is needed at all.
    if (found.files.length === 0) return;
    // Route param is the first member's id — the anchor DicomViewerPage
    // uses for a direct-URL/refresh fallback fetch when router state isn't
    // available (see DicomViewerPage.tsx); the full series (every member,
    // in order) is passed via state so the normal click path never needs
    // that fallback fetch at all.
    navigate(`/patient/diagnostics/imaging/${found.files[0].id}`, { state: { series: found } });
  };

  if (isLoading) {
    return (
      <div className={cn("rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm", className)}>
        <h3 className="text-sm font-extrabold text-[#0A2947]">Recent DICOM Uploads</h3>
        <div className="mt-4 flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl border border-[#E5E7EB] px-4 py-3">
              <Skeleton.Avatar active size={36} shape="circle" />
              <div className="min-w-0 flex-1">
                <Skeleton active title={{ width: "50%" }} paragraph={{ rows: 1, width: "30%" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (recent.length === 0 && activeBatches.length === 0) return null;

  return (
    <div className={cn("rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm", className)}>
      <h3 className="text-sm font-extrabold text-[#0A2947]">Recent DICOM Uploads</h3>
      <InfiniteScrollContainer className={cn("mt-4", SCROLL_HEIGHT_CLASSES)}>
        <div className="flex flex-col gap-3 pr-1">
          {activeBatches.map((batch) => (
            <ActiveBatchRow key={batch.batchId} batch={batch} />
          ))}
          {recent.map((s) => (
            <button
              // NOT s.seriesInstanceUid — see getSeriesGroupId's own docstring.
              key={getSeriesGroupId(s)}
              type="button"
              onClick={() => openViewer(s)}
              className="flex w-full items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3 text-left transition-colors hover:border-[#F36F8F]/40 hover:bg-[#FFF9FA]"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F7F7F7]">
                <Icon icon="lucide:scan-line" className="h-4 w-4 !text-[#0A2947]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-[#0A2947]">
                  {s.title || s.files[0]?.originalFilename || "Untitled study"}
                </p>
                <p className="text-xs text-[#9CA3AF]">
                  {s.modality ? `${s.modality} · ` : ""}
                  {formatDate(s.createdAt)}
                  {s.fileCount > 1 ? ` · ${s.fileCount} files` : s.frameCount > 1 ? ` · ${s.frameCount} frames` : ""}
                </p>
              </div>
              <Icon icon="lucide:chevron-right" className="h-4 w-4 shrink-0 !text-[#9CA3AF]" />
            </button>
          ))}
        </div>
      </InfiniteScrollContainer>
    </div>
  );
}
